import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { ArrowRight } from "lucide-react"
import {
  BoxStaffPermissionsModal,
  type BoxStaffPermissionsModalRef,
} from "@modules/boxStaff/components/boxStaffPermissionsModal"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useAddBoxStaff } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { useRef } from "react"
import { z } from "zod"

const addStaffSchema = z
  .object({
    userId: z.string().nullable(),
    userEmail: z.email(word("global.input.email.error")).nullable(),
    role: z.enum(BoxStaffRole),
  })
  .refine((value) => !!value.userId || !!value.userEmail, {
    error: word("box.staff.addBoxStaffInput.emailOrId.error"),
    path: ["userId"],
  })

type Props = {
  boxStaffRole: Exclude<BoxStaffRole, "READ_ONLY">
  className?: string
} & React.ComponentProps<"div">

export function BoxStaffAddForm({ boxStaffRole, className, ...props }: Props) {
  const boxStaffPermissionsModal = useRef<BoxStaffPermissionsModalRef>(null)
  const { addBoxStaff, isPending } = useAddBoxStaff()
  const { hideSidebar } = useSidebar()

  const form = useAppForm({
    defaultValues: {
      userId: null as string | null,
      userEmail: null as string | null,
      role: boxStaffRole as BoxStaffRole,
    },
    validators: {
      onSubmit: addStaffSchema,
    },
    onSubmit: async ({ value }) => {
      await addBoxStaff(value)
        .then(() => {
          toast.success(word("box.staff.addForm.toast.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar()
        })
        .catch((error: unknown) => {
          if (typeof error === "object" && error !== null && "status" in error) {
            if (error.status === 409) {
              toast.error(word("box.staff.addForm.toast.errorAlreadyStaffMember"), {
                classNames: errorToastClassName(),
              })
              return
            } else {
              toast.error(word("box.staff.addForm.toast.error"), {
                classNames: errorToastClassName(),
              })
            }
          } else {
            toast.error(word("box.staff.addForm.toast.error"), {
              classNames: errorToastClassName(),
            })
          }
        })
    },
  })

  const handleShowPermissions = () => {
    boxStaffPermissionsModal.current?.open()
  }

  return (
    <>
      <div className={cn("flex w-full flex-col gap-8 px-5 pt-16", className)} {...props}>
        {/* HEADER */}
        <div className="flex flex-col gap-2">
          <Text fontStyle="TL" className="text-textDefault-heading">
            {`${word("add.box.member.cardsView.add")} ${displayRole(boxStaffRole).toLowerCase()}`}
          </Text>
          <Text fontStyle="BM" className="text-textDefault-body">
            {word(`box.staff.permissions.description.${boxStaffRole}`)}
          </Text>
        </div>

        <div className="flex w-full flex-col gap-4">
          {/* SEARCH STAFF */}
          <form.AppField name="userId">
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.staff.addForm.label.name")}</field.Label>
                <field.Content>
                  <field.Description>{word("box.staff.addForm.label.description")}</field.Description>
                  <field.SearchUser
                    searchDescriptionText={word("boxSessionAddSubscriberModal.dialog.empytemplate.body")}
                    tooltipContentProps={{ className: "max-w-50", side: "left" }}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          {/* PERMISSIONS */}
          <HuPermissions
            title={word("box.staff.table.dayToDay.permissions.title")}
            role={boxStaffRole}
            button={
              <Button variant="underline" size="lg" onClick={handleShowPermissions} className="ml-auto">
                {word("box.staff.table.dayToDay.button.seeDetails")}
                <ArrowRight size={14} />
              </Button>
            }
          />
        </div>
        <div></div>
        {/* BUTTON */}
        <div className="mb-6 flex flex-row items-center justify-center gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              hideSidebar()
            }}
            className="flex-1"
          >
            {word("global.cancel")}
          </Button>

          <form.Subscribe selector={(state) => [state.values.userId, state.values.userEmail] as const}>
            {([userId, userEmail]) => (
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  void form.handleSubmit()
                }}
                disabled={(!userEmail && !userId) || isPending}
                className="flex-1"
              >
                {word("add.box.member.cardsView.add")} {displayRole(boxStaffRole).toLowerCase()}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </div>
      <BoxStaffPermissionsModal ref={boxStaffPermissionsModal} />
    </>
  )
}
